import { Column } from "primereact/column";
import React, { useCallback, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  PageHeader,
  Table,
  useTreeData,
  AdvancedFilterColumn,
} from "@velocity-ehs/component-library";
import {
  defaultPagination,
  SortDirection,
} from "../../supportFunctions/graphql/Queries";
import { useTranslation } from "react-i18next";
import * as Permissions from "../../supportFunctions/Permissions";
import UsersService from "@velocity-ehs/user-utils-module";
import { StatusBody } from "./StatusBody";
import { useGetPersons } from "../../../hooks/useGetPersons";
import classes from "./PersonForm.module.scss";
import { isEqual } from "lodash";
import { PersonStatusOptions } from "./Person";
import { FilterMatchMode } from "primereact/api";

const defaultTableParams = {
  ...defaultPagination,
  rows: 20,
  sortField: "displayName",
  sortOrder: SortDirection.ASC,
  filters: {
    firstName: {
      value: null,
    },
    lastName: {
      value: null,
    },
    email: {
      value: null,
    },
    middleName: { value: null },
    displayName: { value: null },
    primaryLocation: { value: [] },
    status: {
      value: [],
    },
  },
};

const initialStatusFilter = [PersonStatusOptions[0]];

const Persons: React.FC<{ editPermission: string[] }> = ({
  editPermission = [],
}) => {
  let history = useHistory();
  const { t } = useTranslation();
  const [lazyParams, setLazyParams] = useState({
    ...defaultTableParams,
    filters: {
      ...defaultTableParams.filters,
      status: { value: initialStatusFilter },
    },
  });
  const { persons, isLoading, pagination } = useGetPersons(lazyParams);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const emptyFieldValue = "—";
  const { treeStore, isAllFetched: isTreeLoaded } = useTreeData();

  // Set total records for pagination and only update when
  // pagination.totalElements is truthy
  useEffect(() => {
    pagination.totalElements && setTotalRecords(pagination.totalElements);
  }, [pagination]);

  const canAddOrEdit = (): boolean => {
    return Permissions.IsAllowed(editPermission, UsersService.permissions);
  };

  const onChange = (event: any) => {
    setLazyParams(event);
  };

  const onRowSelect = (event: any): void => {
    history.push(`/person/${event.data.id}/edit`);
  };

  const onGlobalSearch = (event: any): void => {
    const value = event.target.value;
    setLazyParams((state: any) => {
      return {
        ...state,
        ...defaultPagination,
        filters: {
          ...state.filters,
          firstName: {
            value,
          },
          lastName: {
            value,
          },
          email: {
            value,
          },
          middleName: {
            value,
          },
          displayName: {
            value,
          },
        },
      };
    });
  };

  const onGlobalSearchCallback = useCallback((event: any) => {
    onGlobalSearch(event);
  }, []);

  const onLocationsFilter = (locationIds: string[]) => {
    setLazyParams((state: any) => {
      return {
        ...state,
        ...defaultPagination,
        filters: {
          ...state.filters,
          primaryLocation: { value: locationIds },
        },
      };
    });
  };

  const getLocationName = (primaryLocation: string) => {
    const locationsList = treeStore.active.flatTree;
    const locationName = locationsList?.find(
      (location) => primaryLocation === location.id
    );
    return locationName?.label ?? "";
  };

  const primaryLocationBody = (rowData: any) => {
    if (!rowData.primaryLocation) {
      return emptyFieldValue;
    }
    const locationName = getLocationName(rowData.primaryLocation);
    return <span className={classes.truncate}>{locationName}</span>;
  };

  const getEmailValue = (rowData: any) => {
    if (rowData.email) {
      return <span>{rowData.email}</span>;
    }
    return emptyFieldValue;
  };

  const getLoginNameValue = (rowData: any) => {
    if (rowData.user?.displayedLoginName) {
      return <span>{rowData.user.displayedLoginName}</span>;
    }
    return emptyFieldValue;
  };

  const handleClearFilters = () => {
    setLazyParams(defaultTableParams);
  };

  const isDefaultFilter = isEqual(
    defaultTableParams.filters,
    lazyParams.filters
  );

  const onStatusFilter = (status: Array<"Active" | "Inactive">) => {
    setLazyParams((state: any) => ({
      ...state,
      filters: {
        ...state.filters,
        status: { value: status, matchMode: FilterMatchMode.EQUALS },
      },
    }));
  };

  const actionButtons = (): Array<any> => {
    return [
      {
        label: t("universal_User"),
        command: () => history.push("/persons/addUser"),
      },
      {
        label: t("universal_non_user"),
        command: () => history.push("/persons/addPerson"),
      },
    ];
  };

  return (
    <>
      <PageHeader
        title={t("universal_persons_users")}
        navigationTree={[
          { text: `Accelerate ${t("universal_Home")}`, href: "/" },
          { text: t("universal_persons_users"), href: "" },
        ]}
        primaryButton={{
          label: t("universal_Add"),
          id: "addUser",
          items: actionButtons(),
          visible: canAddOrEdit(),
          icon: "pi-chevron-down",
          buttonClass: "p-button",
        }}
      />
      <div className="card__fullContent">
        <Table
          data={persons}
          params={lazyParams}
          totalRecords={totalRecords}
          selectionMode="single"
          onRowSelect={onRowSelect}
          onPage={onChange}
          onSort={onChange}
          onGlobalSearch={onGlobalSearchCallback}
          isLoading={isLoading || !isTreeLoaded}
          showLocationsFilter
          onLocationsFilter={onLocationsFilter}
          clearFilterCallback={handleClearFilters}
          isClearFiltersButtonDisabled={isDefaultFilter}
          testId="persons-table"
        >
          <Column
            field="displayName"
            header={
              <span className={classes.capitalize}>
                {t("universal_display_name")}
              </span>
            }
            sortable
          />
          <Column
            field="firstName"
            header={
              <span className={classes.capitalize}>
                {t("universal_First_Name")}
              </span>
            }
            sortable
          />

          <Column
            field="lastName"
            header={
              <span className={classes.capitalize}>
                {t("universal_Last_Name")}
              </span>
            }
            sortable
          />

          <Column
            field="primaryLocation"
            header={
              <span className={classes.capitalize}>
                {t("universal_primary_location")}
              </span>
            }
            body={primaryLocationBody}
          />

          <Column
            field="email"
            sortable
            header={t("universal_Email")}
            body={getEmailValue}
          />
          <Column
            field="displayedLoginName"
            header={t("universal_Username")}
            body={getLoginNameValue}
          />
          {AdvancedFilterColumn({
            field: "status",
            filterField: "status",
            header: t("person_user_status"),
            options: PersonStatusOptions,
            body: StatusBody,
            onApply: onStatusFilter,
            filterParams: lazyParams.filters,
          })}
        </Table>
      </div>
    </>
  );
};

export default Persons;
